import {
    ExpenseGet,
    ExpenseStatus,
    ExpenseTypeGet,
    Receipt,
    ReceiptAttachmentProcessRes,
    SpscExpenseTypeGet,
    SpscGet,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, Signal } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";

export interface ProcessSubscriber {
    skipIds: string[];
}

export interface ReceiptSliceParams extends ApiSliceParams {
    subscribers: Record<string, ProcessSubscriber>;
    uploadedReceiptIds: string[];
    expenses: ReadonlySignal<Expense[]>;
}

export interface OcrSliceParams extends ApiSliceParams {
    mapCode: (codes: number[]) => ExpenseType | null;
}

// @TODO type ReceiptAttachmentGetRes is not exported from com lib
export interface ReceiptAttachment {
    id: string;
    blob_id: Readonly<Ulid>;
    url: string;
    size: number;
    expires: number;
    type: string;
    // if attachment is an image it will have single thumbnail
    // pdf attachment will have thumbnail for each page
    thumbnails: Signal<string[]>;
    thumbnailsLoaded: Signal<boolean>;
}

export interface ReceiptAttachmentRef {
    id: string;
    blob_id: Readonly<Ulid>;
    attachmentId: string;
    receiptId: string;
    expenseId: string;
}

export type ExpenseReceipt = Omit<Receipt, "id" | "attachments"> & {
    id: string;
    _id: Readonly<Ulid>;
    attachments: Signal<ReceiptAttachment[]>;
    _attachments: readonly Readonly<Ulid>[];
    tag: Signal<number>;
    loading: Signal<boolean>;
};

export const enum ExpenseViewStatus {
    NotSubmitted = 0,
    Submitted = 1,
    Approved = 2,
    BookedPaid = 3,
    BookedUnpaid = 4,
    Deleted = 5,
}

export type Expense = Omit<ExpenseGet, "id" | "status"> & {
    id: string;
    _id: Readonly<Ulid>;
    status: ExpenseViewStatus;
    _status: ExpenseStatus;
    receipts: Signal<ExpenseReceipt[]>;
    loading: Signal<boolean>;
    loaded: Signal<boolean>;
};

export type ExpenseType = Omit<ExpenseTypeGet, "id"> & {
    id: string;
    _id: Readonly<Ulid>;
};

export interface CreateExpenseRequest {
    description?: string;
    status?: ExpenseStatus;
}

export interface UpdateExpenseRequest {
    id: Readonly<Ulid>;
    description?: string;
    status?: ExpenseStatus;
}

export interface CreateReceiptRequest {
    expense: Readonly<Ulid>;
    currency?: Readonly<Ulid>;
    amount?: number;
    base_vat?: number;
    quantity?: number;
    comment?: string;
    date?: Date;
    expenseType?: Readonly<Ulid>;
    project?: Readonly<Ulid>;
    purchaseCode?: Readonly<Ulid>;
    rate?: number;
    company_card?: boolean;
}

export interface UpdateReceiptRequest {
    id: Readonly<Ulid>;
    amount?: number;
    base_vat?: number;
    comment?: string;
    currency?: Readonly<Ulid>;
    date?: Date;
    expense_type?: Readonly<Ulid>;
    project?: Readonly<Ulid>;
    purchase_code?: Readonly<Ulid>;
    quantity?: number;
    rate?: number;
    company_card?: boolean;
}

export interface AddAttachmentRequest {
    blob: Blob;
    name?: string;
    lastModified?: number;
    type?: string;
    receipt: Readonly<Ulid>;
}

export interface AddAttachmentParams {
    onProgress?: (loaded: number, total: number) => void;
    process?: boolean;
}

interface NumericFigure {
    type: "number";
    value: number;
}

interface TextFigure {
    type: "text";
    value: string;
}

interface DateFigure {
    type: "date";
    value: Date;
}

interface BaseFigure {
    boundaries: number[];
    score: number;
    label: string;
}

export type FigureKey = keyof FiguresMap;

export type Figure = (NumericFigure | TextFigure | DateFigure) & BaseFigure;

export interface FiguresMap {
    date?: DateFigure & BaseFigure;
    total?: NumericFigure & BaseFigure;
    vat?: NumericFigure & BaseFigure;
    subtotal?: NumericFigure & BaseFigure;
    category?: TextFigure & BaseFigure;
    currency?: TextFigure & BaseFigure;
}

export interface AttachmentProcess {
    attachmentId: string;
    blobId: string;
    figures: Figure[];
    figuresMap: FiguresMap;
    pages: { width: number; height: number }[];
    type?: ExpenseType | null;
    raw: ReceiptAttachmentProcessRes;
}

export interface SpsCode extends SpscGet {}

export interface SpsCodeMapItem extends Omit<SpscExpenseTypeGet, "id"> {
    id: string;
    _id: Readonly<Ulid>;
}

// @TODO: AttachmentDuplicateState is not exported from com lib
export enum AttachmentDuplicateState {
    Unique = 0,
    Duplicate = 1,
    SelfDuplicate = 2,
    UNRECOGNIZED = -1,
}

export interface Duplicate {
    id: string;
    _id: Readonly<Ulid>;
    state: AttachmentDuplicateState;
}
